import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { buildProductUrl, buildVisitFormUrl, buildAppointmentUrl } from '@assets/i18n/locales';
import { ButtonComponent } from '@components/button/button.component';
import { CosentinoSvgComponent } from '@components/cosentino-svg/cosentino-svg.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService, UserInfo } from 'app/services/api.service';

export interface NewsCard {
    title: string;
    image?: string;
    link?: string;
    action?: () => void;
}

type RoleKey = 'FABRICATOR' | 'INSTALLER' | 'RETAIL' | 'ARCHITECT' | 'DESIGNER' | 'DEFAULT';

@Component({
    selector: 'app-news',
    templateUrl: './news.component.html',
    imports: [ButtonComponent, CommonModule, TranslateModule, CosentinoSvgComponent],
    standalone: true,
    styleUrls: ['./news.component.scss']
})
export class NewsComponent implements OnInit {
    userInfo: UserInfo
    newsCards: NewsCard[] = []
    hoveredCard: any = null

    private europeCodes = new Set(['ES','PT','IT','FR','GB','IR','DE','AT','BE','SE','DK','NO','CZ','CH','FI','HU','NL','PL','LT','LV','EE','LU','SI','SK']);
    private amerasiaCodes = new Set(['US','CA','NZ','SG','ZA','TR','AE','IL','MY','MX','CO','AR','CL','BR','JA']);
    private australiaCodes = new Set(['AU']);
    
    constructor(private api: ApiService, private translate: TranslateService) {}

    private translations() {
        this.translate.get([
            'news.title_n1',
            'news.title_n2',
            'news.title_n3',
            'news.title_n4',
            'news.title_n5',
            'news.title_n6',
            'news.title_n7',
            'news.title_n8',
            'news.title_n9',
            'news.title_n10',
        ])
    }
    
    ngOnInit(): void {
        this.api.userInfo.subscribe(async userInfo => {
            this.userInfo = userInfo;
            const roleKey = (userInfo.accountType as unknown as keyof RoleKey) || 'DEFAULT';
            const normalizedRole: RoleKey = (roleKey as unknown as string).toUpperCase() as RoleKey;

            const country = userInfo.country?.toUpperCase?.() ?? '';
            const userLang = userInfo.language ?? undefined;

            const NEWS = await this.buildNews(country, userLang);
            this.newsCards = NEWS[normalizedRole] ?? NEWS['DEFAULT'];
        });
    }


    openExternal(link?: string): void {
        if (!link) return
        window.open(link, '_blank', 'noopener,noreferrer')
    }

    private async buildNews(countryCode: string, userLang?: string) {
        const getTranslation = (key: string): Promise<string> =>
            this.translate.get(key).toPromise();

        const n = async (key: string, image?: string, link?: string): Promise<NewsCard> => ({
            title: await getTranslation(key),
            image,
            link,
            action: link ? () => this.openExternal(link) : undefined,
        });

        const sumaUrl = buildProductUrl(countryCode, 'suma', userLang);
        const sensaUrl = buildProductUrl(countryCode, 'sensa', userLang);
        const pietraUrl = buildProductUrl(countryCode, 'pietrakode', userLang);
        const leChicUrl = buildProductUrl(countryCode, 'le-chic', userLang);
        const ukiyoUrl = buildProductUrl(countryCode, 'ukiyo', userLang);
        const blogUrl = buildProductUrl(countryCode, 'blog', userLang);

        const visitaUrl = buildVisitFormUrl(countryCode, userLang);

        const vanitiesUrl = buildProductUrl(countryCode, 'vanities', userLang);
        const galleriesUrl = buildProductUrl(countryCode, 'galleries', userLang);

        const appointmentUrl = buildAppointmentUrl(countryCode, userLang);

        const ALL: { [k: string]: NewsCard } = {
            n1: await n('news.title_n1', 'https://assetsstatic.cosentino.com/ImagesCommunityCosentinoWe/newsPortal/FAB/Suma.png', sumaUrl),
            n2: await n('news.title_n2', 'https://assetsstatic.cosentino.com/ImagesCommunityCosentinoWe/newsPortal/K&B/SensaNaori.png', sensaUrl),
            //n3: await n('news.title_n3', 'assets/images/news/VisitaVirtual.png', visitaUrl),
            n4: await n('news.title_n4', 'https://assetsstatic.cosentino.com/ImagesCommunityCosentinoWe/newsPortal/K&B/PietraKode.png', pietraUrl),
            n5: await n('news.title_n5', 'assets/images/news/Zoe.png', vanitiesUrl),
            n6: await n('news.title_n6', 'https://assetsstatic.cosentino.com/ImagesCommunityCosentinoWe/newsPortal/CTOP/2x/Captura%20de%20pantalla%202023-07-10%20a%20las%2012.10%201.png', galleriesUrl),
            n7: await n('news.title_n7', 'https://assetsstatic.cosentino.com/ImagesCommunityCosentinoWe/newsPortal//CTOP/2x/Lechic.png', leChicUrl),
            n8: await n('news.title_n8', 'assets/images/news/Ukiyo.jpg', ukiyoUrl),
            n9: await n('news.title_n9', 'https://assetsstatic.cosentino.com/ImagesCommunityCosentinoWe/newsPortal/CTOP/2x/Captura%20de%20pantalla%202023-07-10%20a%20las%2014.06%201.png', blogUrl)
            // n10: await n('news.title_n10', 'assets/images/news/PNBrasil.png', appointmentUrl)
          
        }

        const isEurope = this.europeCodes.has(countryCode);
        const isAmerasia = this.amerasiaCodes.has(countryCode);
        const isAustralia = this.australiaCodes.has(countryCode);

        let architectCards: NewsCard[];
        let designerCards: NewsCard[];

        if (isEurope) {
            architectCards = [ALL['n1'], ALL['n6'], ALL['n2']];
            designerCards = [ALL['n1'], ALL['n6'], ALL['n2']];
        } else if (isAmerasia) {
            architectCards = [ALL['n6'], ALL['n8'], ALL['n7'], ALL['n9']];
            designerCards = [ALL['n6'], ALL['n8'], ALL['n7'], ALL['n9']];
        } else if (isAustralia) {
            architectCards = [ALL['n6'], ALL['n8'], ALL['n2'], ALL['n9']];
            designerCards = [ALL['n6'], ALL['n8'], ALL['n2'], ALL['n9']];
        } else {
            architectCards = [ALL['n1'], ALL['n2'], ALL['n4']];
            designerCards = [ALL['n1'], ALL['n2'], ALL['n4']];
        }

        let fabricatorCards: NewsCard[];

        const n10Card = ALL['n10'];

        if (['US', 'CA', 'BR'].includes(countryCode) && n10Card) {
            fabricatorCards = [ALL['n5'], ALL['n1'], ALL['n2'], n10Card];
        } else if (isEurope || isAmerasia) {
            fabricatorCards = [ALL['n5'], ALL['n1'], ALL['n2']];
        } else if (isAustralia) {
            fabricatorCards = [ALL['n8'], ALL['n2'], ALL['n4']];
        } else {
            fabricatorCards = [ALL['n1'], ALL['n2'], ALL['n4']];
        }

        let installerCards: NewsCard[];

        if (isEurope || isAmerasia) {
            installerCards = [ALL['n5'], ALL['n1'], ALL['n2']];
        } else if (isAustralia) {
            installerCards = [ALL['n8'], ALL['n2'], ALL['n4']];
        } else {
            installerCards = [ALL['n1'], ALL['n2'], ALL['n5'], ALL['n4']];
        }

        let retailCards: NewsCard[];
        if (isEurope) {
            retailCards = [ALL['n5'], ALL['n1'], ALL['n2']];
        } else if (isAmerasia) {
            retailCards = [ALL['n5'], ALL['n1'], ALL['n2']];
        } else if (isAustralia) {
            retailCards = [ALL['n2'], ALL['n4']];
        } else {
            retailCards = [ALL['n1'], ALL['n6'], ALL['n7'], ALL['n8']];
        }

        return {
                FABRICATOR: fabricatorCards,
                INSTALLER: installerCards,
                RETAIL: retailCards,
                ARCHITECT: architectCards,
                DESIGNER: designerCards,
                DEFAULT: [ALL['n1'], ALL['n2'], ALL['n4']],
            } as Record<RoleKey, NewsCard[]>;
    }
}